import { Cause, Deferred, Effect, Exit, Fiber, Queue, Ref, Scope } from "effect";
import { makeRequestScopes, type OwnedRequest } from "./request-scopes.ts";
/** Resident entries keep a bounded LRU. An evicted resource stays alive until
 * its borrowers release it; closing joins loaders, borrowers and resource cleanup. */
export const makeScopedCache = Effect.fn("Frontend.ScopedCache")(function* <K, A, E>(capacity: number, load: (key: K) => Effect.Effect<A, E, Scope.Scope>, retain: (cause: Cause.Cause<never>) => Effect.Effect<void>, options: {
  readonly releaseIdle?: boolean;
} = {}) {
  const owner = yield* Scope.Scope, preserveFinalizerFailures = yield* Scope.PreserveFinalizerFailures, scopes = yield* makeRequestScopes(owner, exit => Exit.isFailure(exit) ? retain(exit.cause) : Effect.void);
  interface Entry {
    readonly key: K;
    readonly owned: OwnedRequest;
    readonly resource: Scope.Closeable;
    readonly ready: Deferred.Deferred<Exit.Exit<A, E>>;
  }
  interface Member {
    readonly entry: Entry;
    readonly leases: number;
    readonly retired: boolean;
  }
  interface State {
    readonly closed: boolean;
    readonly resident: ReadonlyMap<K, Entry>;
    readonly members: ReadonlyMap<object, Member>;
  }
  const state = yield* Ref.make<State>({ closed: false, resident: new Map(), members: new Map() }), completions = yield* Queue.unbounded<Effect.Effect<void>>();
  yield* Effect.addFinalizer(() => Queue.shutdown(completions));
  yield* Effect.forkIn(Effect.forever(Queue.take(completions).pipe(Effect.flatMap(work => work.pipe(Effect.catchCause(retain))))), owner);
  const close = (entry: Entry) => Effect.uninterruptible(Effect.gen(function* () {
    const result = yield* Effect.exit(entry.owned.close);
    yield* Ref.update(state, current => { const members = new Map(current.members); members.delete(entry.owned.identity); return { ...current, members }; });
    if (Exit.isFailure(result))
      yield* retain(result.cause);
  }));
  const closeUnused = (entry: Entry) => Effect.suspend(() => Ref.get(state).pipe(Effect.flatMap(current => { const member = current.members.get(entry.owned.identity); return member && member.retired && member.leases === 0 ? close(entry) : Effect.void; })));
  yield* Effect.addFinalizer(() => Effect.uninterruptible(Effect.gen(function* () {
    const current = yield* Ref.getAndUpdate(state, value => ({ ...value, closed: true, resident: new Map() }));
    yield* Effect.forEach(current.members.values(), member => close(member.entry), { discard: true, concurrency: "unbounded" });
  })));
  const release = (entry: Entry) => Effect.uninterruptible(Effect.gen(function* () {
    const retiring = yield* Ref.modify(state, current => {
      const member = current.members.get(entry.owned.identity);
      if (!member)
        return [false, current] as const;
      const members = new Map(current.members), leases = member.leases - 1;
      const retired = member.retired || options.releaseIdle === true && leases === 0;
      const resident = new Map(current.resident);
      if (retired && resident.get(entry.key)?.owned.identity === entry.owned.identity)
        resident.delete(entry.key);
      members.set(entry.owned.identity, { ...member, leases, retired });
      return [retired && leases === 0, { ...current, resident, members }] as const;
    });
    if (retiring)
      yield* close(entry);
  }));
  const acquire = Effect.fn("Frontend.ScopedCache.acquire")((key: K) => Effect.uninterruptible(Effect.gen(function* () {
    if (owner.state._tag === "Closed" || (yield* Ref.get(state)).closed)
      return yield* Effect.interrupt;
    const cached = yield* Ref.modify(state, (current): readonly [
      Entry | null,
      State
    ] => {
      if (current.closed || owner.state._tag === "Closed")
        return [null, current];
      const hit = current.resident.get(key), member = hit ? current.members.get(hit.owned.identity) : undefined;
      if (!hit || !member)
        return [null, current];
      const resident = new Map(current.resident), members = new Map(current.members);
      resident.delete(key);
      resident.set(key, hit);
      members.set(hit.owned.identity, { ...member, leases: member.leases + 1 });
      return [hit, { ...current, resident, members }];
    });
    if (cached)
      return cached;
    const owned = yield* scopes.open, resource = yield* Scope.fork(owned.scope), ready = yield* Deferred.make<Exit.Exit<A, E>>();
    const candidate: Entry = { key, owned, resource, ready };
    interface Admission {
      readonly entry: Entry;
      readonly inserted: boolean;
      readonly evicted: readonly Entry[];
    }
    const admission = yield* Ref.modify(state, (current): readonly [
      Admission | null,
      State
    ] => {
      if (current.closed || owner.state._tag === "Closed")
        return [null, current];
      const hit = current.resident.get(key), member = hit ? current.members.get(hit.owned.identity) : undefined, resident = new Map(current.resident), members = new Map(current.members);
      if (hit && member) {
        resident.delete(key);
        resident.set(key, hit);
        members.set(hit.owned.identity, { ...member, leases: member.leases + 1 });
        return [{ entry: hit, inserted: false, evicted: [] }, { ...current, resident, members }];
      }
      resident.set(key, candidate);
      members.set(owned.identity, { entry: candidate, leases: 1, retired: false });
      const evicted: Entry[] = [];
      while (resident.size > capacity) {
        const oldest = resident.keys().next();
        if (oldest.done)
          break;
        const entry = resident.get(oldest.value);
        resident.delete(oldest.value);
        if (!entry)
          continue;
        const currentMember = members.get(entry.owned.identity);
        if (!currentMember)
          continue;
        members.set(entry.owned.identity, { ...currentMember, retired: true });
        if (currentMember.leases === 0)
          evicted.push(entry);
      }
      return [{ entry: candidate, inserted: true, evicted }, { ...current, resident, members }];
    });
    if (admission === null) {
      yield* owned.close;
      return yield* Effect.interrupt;
    }
    if (!admission.inserted)
      yield* owned.close;
    else {
      const loading = Effect.exit(Effect.suspend(() => load(key)).pipe(Effect.provideService(Scope.Scope, resource))).pipe(Effect.flatMap(exit => Effect.gen(function* () {
        if (Exit.isFailure(exit)) {
          yield* retain(Cause.fromReasons(exit.cause.reasons.filter(reason => reason._tag === "Die")));
          yield* Ref.update(state, current => {
            const resident = new Map(current.resident);
            if (resident.get(key)?.owned.identity === owned.identity)
              resident.delete(key);
            const members = new Map(current.members), member = members.get(owned.identity);
            if (member)
              members.set(owned.identity, { ...member, retired: true });
            return { ...current, resident, members };
          });
        }
        yield* Deferred.succeed(ready, exit);
        if (Exit.isFailure(exit))
          yield* Effect.sync(() => { Queue.offerUnsafe(completions, closeUnused(candidate)); });
      })));
      yield* Effect.forkIn(loading, owned.scope, { uninterruptible: false });
    }
    for (const entry of admission.evicted)
      yield* Effect.sync(() => { Queue.offerUnsafe(completions, closeUnused(entry)); });
    return admission.entry;
  })));
  const use = <B, E2, R>(key: K, body: (value: A) => Effect.Effect<B, E2, R>): Effect.Effect<B, E | E2, R> => Effect.uninterruptibleMask(restore => Effect.gen(function* () {
    const entry = yield* acquire(key);
    const borrower = yield* Effect.forkIn(Effect.exit(Effect.gen(function* () { const value = yield* (yield* Deferred.await(entry.ready)); return yield* body(value); })), entry.owned.scope, { uninterruptible: false });
    return yield* restore(Fiber.join(borrower)).pipe(Effect.flatMap(result => result), Effect.ensuring(Effect.gen(function* () {
      yield* Fiber.interrupt(borrower);
      const result = yield* Fiber.await(borrower);
      if (Exit.isFailure(result))
        yield* retain(Cause.fromReasons(result.cause.reasons.filter(reason => reason._tag === "Die")));
      else if (Exit.isFailure(result.value))
        yield* retain(Cause.fromReasons(result.value.cause.reasons.filter(reason => reason._tag === "Die")));
      yield* release(entry);
    })));
  })).pipe(Effect.provideService(Scope.PreserveFinalizerFailures, preserveFinalizerFailures));
  const inspect = Ref.get(state).pipe(Effect.map(current => ({ keys: [...current.resident.keys()], members: current.members.size, leases: [...current.members.values()].reduce((sum, member) => sum + member.leases, 0), closed: current.closed })));
  return { use, inspect };
});
